import { useEffect, useRef } from "react";

declare global {
  interface Window {
    THREE?: any;
  }
}

let threeLoader: Promise<void> | null = null;

function ensureThree(): Promise<void> {
  if (window.THREE) return Promise.resolve();
  if (threeLoader) return threeLoader;

  threeLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-ext-three='1']") as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Falha ao carregar Three.js")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://unpkg.com/three@0.160.0/build/three.min.js";
    script.async = true;
    script.dataset.extThree = "1";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Falha ao carregar Three.js"));
    document.head.appendChild(script);
  });

  return threeLoader;
}

export default function HeroThreeScene() {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let disposed = false;
    let frame = 0;
    const cleanups: Array<() => void> = [];
    const disposables: any[] = [];

    ensureThree()
      .then(() => {
        if (disposed || !mountRef.current || !window.THREE) return;
        const THREE = window.THREE;
        const mount = mountRef.current;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(42, mount.clientWidth / mount.clientHeight, 0.1, 30);
        camera.position.set(0, 0.15, 3.5);

        const renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          powerPreference: "high-performance",
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.7));
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        mount.appendChild(renderer.domElement);

        const ambient = new THREE.AmbientLight(0xffffff, 0.72);
        scene.add(ambient);

        const key = new THREE.DirectionalLight(0xffffff, 1.08);
        key.position.set(2.5, 3.8, 2.2);
        key.castShadow = true;
        key.shadow.mapSize.set(1024, 1024);
        scene.add(key);

        const fill = new THREE.DirectionalLight(0x93c5fd, 0.46);
        fill.position.set(-2.2, 1.8, -1.8);
        scene.add(fill);

        const floor = new THREE.Mesh(
          new THREE.CircleGeometry(2.2, 40),
          new THREE.MeshStandardMaterial({
            color: 0xe2e8f0,
            roughness: 0.88,
            metalness: 0.06,
            transparent: true,
            opacity: 0.7,
          })
        );
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = -1;
        floor.receiveShadow = true;
        scene.add(floor);
        disposables.push(floor.geometry, floor.material);

        const docGroup = new THREE.Group();
        scene.add(docGroup);

        const body = new THREE.Mesh(
          new THREE.BoxGeometry(1.55, 2.05, 0.1),
          new THREE.MeshStandardMaterial({
            color: 0xf8fafc,
            roughness: 0.34,
            metalness: 0.12,
          })
        );
        body.castShadow = true;
        body.receiveShadow = true;
        docGroup.add(body);
        disposables.push(body.geometry, body.material);

        const addLine = (w: number, y: number) => {
          const line = new THREE.Mesh(
            new THREE.BoxGeometry(w, 0.06, 0.015),
            new THREE.MeshStandardMaterial({
              color: 0xff7d70,
              roughness: 0.45,
              metalness: 0.04,
            })
          );
          line.position.set(-0.12, y, 0.06);
          docGroup.add(line);
          disposables.push(line.geometry, line.material);
        };

        addLine(1.12, 0.62);
        addLine(0.92, 0.34);
        addLine(0.68, 0.06);

        const stamp = new THREE.Mesh(
          new THREE.RingGeometry(0.18, 0.24, 30),
          new THREE.MeshStandardMaterial({
            color: 0x5dade2,
            roughness: 0.36,
            metalness: 0.2,
            emissive: 0x5dade2,
            emissiveIntensity: 0.16,
          })
        );
        stamp.position.set(0.36, -0.52, 0.062);
        docGroup.add(stamp);
        disposables.push(stamp.geometry, stamp.material);

        const mouseTarget = { x: 0, y: 0 };
        const mouseCurrent = { x: 0, y: 0 };

        const onMove = (ev: PointerEvent) => {
          const r = mount.getBoundingClientRect();
          const x = (ev.clientX - r.left) / r.width;
          const y = (ev.clientY - r.top) / r.height;
          mouseTarget.x = (x - 0.5) * 0.35;
          mouseTarget.y = (y - 0.5) * 0.22;
        };
        mount.addEventListener("pointermove", onMove);
        cleanups.push(() => mount.removeEventListener("pointermove", onMove));

        const onResize = () => {
          if (!mountRef.current) return;
          const w = mountRef.current.clientWidth;
          const h = mountRef.current.clientHeight;
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h);
          renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.7));
        };
        window.addEventListener("resize", onResize);
        cleanups.push(() => window.removeEventListener("resize", onResize));

        const clock = new THREE.Clock();
        const animate = () => {
          if (disposed) return;
          frame = requestAnimationFrame(animate);
          const t = clock.getElapsedTime();

          mouseCurrent.x += (mouseTarget.x - mouseCurrent.x) * 0.06;
          mouseCurrent.y += (mouseTarget.y - mouseCurrent.y) * 0.06;

          docGroup.rotation.y = 0.18 + mouseCurrent.x;
          docGroup.rotation.x = -0.08 + mouseCurrent.y;
          docGroup.position.y = Math.sin(t * 1.15) * 0.08;

          renderer.render(scene, camera);
        };

        animate();

        cleanups.push(() => {
          cancelAnimationFrame(frame);
          renderer.dispose();
          if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
        });
      })
      .catch(() => {
        // Keep page stable if external script fails.
      });

    return () => {
      disposed = true;
      cleanups.forEach((fn) => fn());
      disposables.forEach((d) => d?.dispose?.());
    };
  }, []);

  return <div className="hero-three-canvas" ref={mountRef} aria-hidden="true" />;
}

